/**
 * Transform Algolia Hits to ProductCardData
 *
 * Converts raw Algolia search hits to the unified ProductCardData format
 * used by the brand ProductCard component.
 */

import type { ProductCardData } from '@/components/brand';
import { getLocaleCode } from '@/lib/algolia/config';
import type { ProductHit, StrapHit, WatchHit } from '@/lib/algolia/types';
import {
  getStrapCardMaterial,
  getStrapCardSubtitle,
  getWatchCardSubtitle,
} from '@/lib/listingData/productCardFields';
import { resolveStrapMaterialSlug } from '@/lib/listingData/plpConfig';
import { buildStrapUrl, buildWatchUrl } from '@/lib/product/urls';
import { getProductBadge } from '@/lib/product/marketing-status';
import { getProductPlaceholder } from '@/lib/product/placeholders';
import { formatPrice } from '@/lib/utils/formatPrice';

/**
 * Type guard for WatchHit
 */
function isWatchHit(hit: ProductHit): hit is WatchHit {
  return hit.product_type === 'Watch';
}

/**
 * Type guard for StrapHit
 */
function isStrapHit(hit: ProductHit): hit is StrapHit {
  return hit.product_type === 'Strap';
}

/**
 * Transform a single Algolia hit to ProductCardData
 */
export function transformHitToProductCard(
  hit: ProductHit,
  locale: string,
  badgeTranslation?: (key: string) => string
): ProductCardData {
  const localeCode = getLocaleCode(locale);
  const productType = hit.product_type;

  // Get localized name
  const name = hit.name?.[localeCode] || hit.name?.['EN_US'] || hit.objectID;

  const subtitle = isStrapHit(hit)
    ? getStrapCardSubtitle(hit, localeCode)
    : isWatchHit(hit)
      ? getWatchCardSubtitle(hit)
      : '';

  // Build PDP URL based on product type. Return a locale-relative pathname
  // (no `/<locale>/` prefix); the consuming `<Link>` from `lib/i18n/navigation`
  // adds the locale because it is configured with `localePrefix: 'always'`.
  // Pre-prefixing here would cause the i18n Link to double-prefix on click.
  let href: string;
  if (isWatchHit(hit)) {
    href = buildWatchUrl(
      hit.collections?.[0]?.slug || 'collection',
      hit.model?.slug || 'model',
      hit.objectID
    );
  } else {
    const materialSlug = resolveStrapMaterialSlug(hit, localeCode);
    href = buildStrapUrl(materialSlug, hit.objectID);
  }

  // Format price
  const price = hit.price;
  const currency = hit.currency || 'USD';
  const formattedPrice =
    price && price > 0
      ? formatPrice(price, currency, locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : '';

  // Get product badge
  let label: string | undefined;
  if (isWatchHit(hit)) {
    const badge = getProductBadge(hit.marketing_status || undefined, hit.launch_date, {
      isPDP: false,
    });
    if (badge && badgeTranslation) {
      label = badgeTranslation(badge.labelKey);
    }
  }

  const material = isStrapHit(hit)
    ? getStrapCardMaterial(hit, localeCode)
    : isWatchHit(hit)
      ? hit.case?.[localeCode] || hit.case?.['EN_US']
      : undefined;

  // Get dial color for watches (used in cart variant display: "Material - Dial Color")
  let dialColor: string | undefined;
  if (isWatchHit(hit)) {
    dialColor = hit.dial_color?.[localeCode] || hit.dial_color?.['EN_US'];
  }

  return {
    id: hit.objectID,
    sku: hit.objectID,
    name,
    subtitle,
    price: formattedPrice,
    priceAmount: price,
    currency,
    image: hit.thumbnail_url || hit.image_url || getProductPlaceholder(productType),
    href,
    variantId: hit.variant_id,
    ctaType: hit.call_to_action,
    label,
    material,
    dialColor,
  };
}

/**
 * Transform multiple Algolia hits to ProductCardData array
 */
export function transformHitsToProductCards(
  hits: ProductHit[],
  locale: string,
  badgeTranslation?: (key: string) => string
): ProductCardData[] {
  return hits.map((hit) => transformHitToProductCard(hit, locale, badgeTranslation));
}
